import { ActionIcon, Button, Drawer, Flex, Text, Tooltip } from '@mantine/core';
import { IconChevronLeft, IconPlus, IconSparkles } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import MobileBottomDrawer from '@/components/ui/MobileBottomDrawer';
import { notificationsStore } from '@/stores/global/RootStore';
import { OrganizationDocumentPreviewPageNotesStore } from '@/stores/local/pages/OrganizationDocumentPreviewPageStore/NotesStore';
import { PdfDocumentNote } from '@/stores/local/shared/PdfNotesStore';

import DocumentNoteDetail from '../DocumentNoteDetail';
import DocumentNoteEditorModal from '../DocumentNoteEditorModal';
import DocumentNotesList from '../DocumentNotesList';

import s from './DocumentNotesDrawer.module.scss';

type DocumentNotesDrawerProps = {
  store: OrganizationDocumentPreviewPageNotesStore;
  isPhone: boolean;
  onOpenPage: (note: PdfDocumentNote) => void;
};

const DocumentNotesDrawer: React.FC<DocumentNotesDrawerProps> = ({ store, isPhone, onOpenPage }) => {
  const activeNote = store.activeNote;
  const isDetail = Boolean(store.detailNoteId && activeNote);
  const isList = !isDetail;

  const title = isDetail ? '' : 'Заметки документа';
  const editorTitle = store.isEditMode ? 'Редактирование заметки' : 'Новая заметка';

  const handleSelectionClick = React.useCallback(() => {
    store.openCreateFromSelection();
  }, [store]);

  const handleListItemOpen = React.useCallback(
    (noteId: string) => {
      store.openDetail(noteId);
    },
    [store],
  );

  const handleEditNote = React.useCallback(
    (noteId: string) => {
      store.openEdit(noteId);
    },
    [store],
  );

  const handleDeleteNote = React.useCallback(
    async (noteId: string) => {
      try {
        const wasAiSummary = store.aiSummaryNote?.id === noteId;

        if (wasAiSummary) {
          notificationsStore.showError('Удаление саммари по заметкам не поддерживается');

          return;
        }

        await store.deleteNote(noteId);
        notificationsStore.showSuccess('Заметка удалена');
      } catch {
        notificationsStore.showError('Не удалось удалить заметку');
      }
    },
    [store],
  );

  const headerRightAddon = isList ? (
    <Flex align="center" gap="xs" wrap="nowrap">
      <Tooltip
        label="Сначала добавьте хотя бы одну заметку"
        disabled={store.canGenerateAiDocumentSummary || store.aiSummaryMeta.isLoading || store.meta.isLoading}
        withArrow
      >
        <Button
          size="xs"
          variant="gradient"
          gradient={{ from: '#56AFFC', to: '#465CFF', deg: 107.33 }}
          leftSection={<IconSparkles size={14} />}
          onClick={store.generateAiDocumentSummary}
          disabled={!store.canGenerateAiDocumentSummary || store.aiSummaryMeta.isLoading || store.meta.isLoading}
          aria-label={
            store.aiSummaryNote
              ? 'Обновить саммари по заметкам к документу'
              : 'Сформировать саммари по заметкам к документу'
          }
        >
          {store.aiSummaryNote ? 'Обновить саммари' : 'Саммари по заметкам'}
        </Button>
      </Tooltip>
      <ActionIcon size="md" onClick={handleSelectionClick} aria-label="Добавить заметку">
        <IconPlus size={16} />
      </ActionIcon>
    </Flex>
  ) : null;

  const backToListButton = isDetail ? (
    <Button
      variant="subtle"
      size="compact-sm"
      className={s.root__back}
      leftSection={<IconChevronLeft size={14} />}
      onClick={store.backToList}
      aria-label="Назад к списку заметок"
    >
      К заметкам
    </Button>
  ) : null;

  const desktopHeader = (
    <div className={s.root__header}>
      <Flex align="flex-start" direction="column" gap={8} className={s['root__header-left']}>
        {backToListButton}
        <Text fw={700} size="lg">
          {title}
        </Text>
      </Flex>
      {headerRightAddon && (
        <Flex align="center" className={s['root__header-actions']}>
          {headerRightAddon}
        </Flex>
      )}
    </div>
  );

  const bodyContent =
    isDetail && activeNote ? (
      <DocumentNoteDetail note={activeNote} onOpenPage={onOpenPage} />
    ) : (
      <DocumentNotesList
        isLoading={store.meta.isLoading}
        notes={store.collection}
        aiSummaryNote={store.aiSummaryNote}
        isAiSummaryGenerating={store.aiSummaryMeta.isLoading}
        aiSummaryStreamPreview={store.aiSummaryStreamPreview}
        activeNoteId={store.activeNoteId}
        canGenerateAiSummary={store.canGenerateAiDocumentSummary}
        onAddNote={handleSelectionClick}
        onOpenNote={handleListItemOpen}
        onEditNote={handleEditNote}
        onDeleteNote={handleDeleteNote}
        onGenerateAiSummary={store.generateAiDocumentSummary}
      />
    );

  const editorModal = <DocumentNoteEditorModal store={store} isPhone={isPhone} title={editorTitle} />;

  const content = (
    <div className={s.root__content}>
      {desktopHeader}
      <div className={s.root__body}>{bodyContent}</div>
    </div>
  );

  if (isPhone) {
    return (
      <>
        <MobileBottomDrawer
          opened={store.drawerOpened}
          onClose={store.closeDrawer}
          title={title ?? ''}
          headerLeftAddon={backToListButton}
          headerAddon={headerRightAddon ?? undefined}
          contentClassName={s.root__mobile}
        >
          <div className={s.root__mobile_body}>{bodyContent}</div>
        </MobileBottomDrawer>
        {editorModal}
      </>
    );
  }

  return (
    <>
      <Drawer
        opened={store.drawerOpened}
        onClose={store.closeDrawer}
        position="left"
        size={520}
        withCloseButton={false}
        classNames={{ content: s.root, body: s['root__drawer-body'] }}
        overlayProps={{ opacity: 0.35, blur: 2 }}
      >
        {content}
      </Drawer>
      {editorModal}
    </>
  );
};

export default observer(DocumentNotesDrawer);
